import { Input } from 'antd';
import CustomButton from 'components/ui/custom-button';
import { EButtonVariant } from 'components/ui/custom-button/enums';
import { AddIcon } from 'assets/icons/add';
import { useNavigate } from 'react-router';
import { ENavigationPath } from 'constants/path';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from 'assets/icons/search';
import CustomTable from 'components/ui/custom-table';
import { binderApi } from 'services/apis';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import ContentIcon from 'assets/icons/content';
import type { TableColumnsType } from 'antd';
import type { BinderTableData } from './types';
import StatusIndicator from 'components/ui/status-indicator';
import { BinderResponseDtoStatusEnum } from 'libs/pinnacle-sdk';
import { useDashboardFiltersStore } from 'stores/dashboard-filters';
import usePagination from 'hooks/usePagination';
import { getLocalDateForMidnightUTC } from 'utils';

export default function BinderTable() {
  const { binderTable: savedFilters, setBinderTableFilters } =
    useDashboardFiltersStore();

  const [inputText, setInputText] = useState(savedFilters.searchText || '');
  const [searchText, setSearchText] = useState(savedFilters.searchText || '');
  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination(10);

  const { t } = useTranslation();

  const columns: TableColumnsType<BinderTableData> = useMemo(
    () => [
      {
        key: 'index',
        title: <ContentIcon />,
        dataIndex: 'index',
        width: '1%',
        align: 'center',
        render: (index: number) => index + 1,
      },
      {
        key: 'binderGroup',
        title: t('binder.table.binderGroup'),
        dataIndex: 'binderGroup',
        width: '15%',
        align: 'left',
        render: (text: string) => <div className="font-semibold">{text}</div>,
      },
      {
        key: 'binderName',
        title: t('binder.table.binderName'),
        dataIndex: 'binderName',
        width: '20%',
        align: 'left',
        render: (text: string) => (
          <div
            style={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxWidth: '15vw',
            }}
          >
            {text}
          </div>
        ),
      },
      {
        key: 'underwritingYear',
        title: t('binder.table.underwritingYear'),
        dataIndex: 'underwritingYear',
        width: '10%',
        align: 'left',
      },
      {
        key: 'startDate',
        title: t('binder.table.startDate'),
        dataIndex: 'startDate',
        width: '10%',
        align: 'left',
      },
      {
        key: 'endDate',
        title: t('binder.table.endDate'),
        dataIndex: 'endDate',
        width: '10%',
        align: 'left',
      },
      {
        key: 'territories',
        title: t('binder.table.territories'),
        dataIndex: 'territories',
        width: '15%',
        align: 'left',
      },
      {
        key: 'status',
        title: t('binder.table.status'),
        dataIndex: 'status',
        width: '15%',
        align: 'left',
        render: (text: BinderResponseDtoStatusEnum) => (
          <StatusIndicator status={text} />
        ),
      },
    ],
    [t]
  );

  const {
    data: { items: binderData, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
  } = useQuery(
    [EQueryKeys.BINDER_LIST, pageSize, currentPage, searchText],
    async () => {
      const { items, pagination } = await binderApi.filterBinders({
        page: currentPage,
        pageSize,
        binderFilterQueryDto: { searchText: searchText },
      });
      return {
        items: items.map((binder, index) => ({
          index: pageSize * (currentPage - 1) + index,
          key: binder.binderId,
          binderGroup: binder.binderGroup,
          binderName: binder.binderName,
          underwritingYear: binder.underwritingYear,
          startDate: binder.authorityStartDate
            ? getLocalDateForMidnightUTC(binder.authorityStartDate).format(
                'DD/MM/YYYY'
              )
            : '-',
          endDate: binder.authorityEndDate
            ? getLocalDateForMidnightUTC(binder.authorityEndDate).format(
                'DD/MM/YYYY'
              )
            : '-',
          territories: binder.territories.join(', '),
          binderId: binder.binderId,
          status: binder.status,
        })),
        pagination,
      };
    }
  );
  const navigate = useNavigate();

  // Save filters to store whenever they change
  useEffect(() => {
    setBinderTableFilters({
      searchText,
    });
  }, [searchText, setBinderTableFilters]);

  const handleSearchTrigger = useCallback(
    () => setSearchText(inputText),
    [inputText]
  );

  return (
    <div className="w-full py-4">
      <div className="flex justify-between mb-4 items-center">
        <div className="text-2xl font-medium">{t('binder.selectBinder')}</div>
        <CustomButton
          className="w-1/5"
          variant={EButtonVariant.PRIMARY}
          label={t('general.createNewRecord')}
          prefixIcon={<AddIcon />}
          onClick={() => navigate(ENavigationPath.NEW_BINDER)}
        />
      </div>
      <div className="mb-4 flex items-center gap-2">
        <Input
          placeholder={t('binder.searchBinder')}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="flex-grow py-[8px]"
          prefix={<SearchIcon />}
          onPressEnter={handleSearchTrigger}
        />
        <CustomButton
          className="w-2/12 py-[8px]"
          variant={EButtonVariant.PRIMARY}
          label={t('general.search')}
          onClick={handleSearchTrigger}
        />
      </div>

      <CustomTable
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onShowSizeChange={onShowSizeChange}
        columns={columns}
        dataSource={binderData || []}
        total={total || 0}
        onRow={(record) => ({
          onClick: () =>
            navigate(
              ENavigationPath.UPDATE_BINDER.replace(':id', record.binderId)
            ),
          className: 'cursor-pointer hover:bg-gray-50',
        })}
      />
    </div>
  );
}
